import {
  addOrUpdateRoute,
  destinationsAvalibleForRoute,
  getRoutesList,
} from "actions/Catalogs";
import InputCustom from "components/InputCustom";
import Modal from "components/Modal";
import Joi from "joi";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useValidator from "react-joi";
import { useDispatch, useSelector } from "react-redux";
import { notify } from "utils";

const RouteModal = ({ openModal, setOpenModal, route }) => {
  const [t] = useTranslation("catalog");
  const [tg] = useTranslation("global");

  const { state, setData, setExplicitField, validate } = useValidator({
    initialData: {
      routeCode: null,
      route: null,
      status: true,
      destinations: [],
    },
    schema: Joi.object({
      routeCode: Joi.string()
        .max(15)
        .required()
        .messages({
          "string.empty": t("error.empty"),
          "string.base": t("error.type", { type: "string" }),
          "string.max": t("error.max", { quantity: "15" }),
          "any.required": t("error.required"),
        }),
      route: Joi.string()
        .max(100)
        .required()
        .messages({
          "string.empty": t("error.empty"),
          "string.base": t("error.type", { type: "string" }),
          "string.max": t("error.max", { quantity: "100" }),
          "any.required": t("error.required"),
        }),
      status: Joi.boolean()
        .required()
        .messages({
          "boolean.base": t("error.type", { type: "boolean" }),
          "any.required": t("error.required"),
        }),
      destinations: Joi.array(),
    }),
    explicitCheck: {
      routeCode: false,
      route: false,
      status: false,
      destinations: false,
    },
    validationOptions: {
      abortEarly: true,
    },
  });
  const routeMemo = useMemo(() => route, [route]);

  const { currentProject } = useSelector((state) => state.auth);
  const { destinationsForRoutes } = useSelector((state) => state.catalog);
  const buttonRef = React.useRef();
  const [selectedIds, setSelectedIds] = useState(() => new Set([]));
  const selectedCount = selectedIds.size;
  const [open, setOpen] = useState(true);
  const [isLoad, setIsLoad] = useState(false);
  const [showError, setShowError] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (routeMemo && routeMemo?.id) {
      setData((old) => ({
        ...old,
        routeCode: routeMemo.routeCode,
        route: routeMemo.route,
        status: routeMemo.status,
        destinations: routeMemo.destinations.map((d) => d.destionationId),
      }));
      setSelectedIds(
        new Set(routeMemo.destinations.map((d) => d.destionationId))
      );
    }
  }, [routeMemo, setData]);

  const destinationsList = useMemo(() => {
    const destinationsMap = new Map();
    if (routeMemo?.destinations) {
      routeMemo.destinations.forEach((dest) => {
        destinationsMap.set(dest.destionationId, dest);
      });
    }

    destinationsForRoutes?.forEach((dest) => {
      if (!destinationsMap.has(dest.destionationId)) {
        destinationsMap.set(dest.destionationId, dest);
      }
    });

    return Array.from(destinationsMap.values());
  }, [destinationsForRoutes, routeMemo]);

  useEffect(() => {
    setData((old) => ({
      ...old,
      destinations: Array.from(selectedIds),
    }));
  }, [selectedIds, setData]);

  const toggleOption = (destionationId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(destionationId)) next.delete(destionationId);
      else next.add(destionationId);
      return next;
    });
  };

  useEffect(() => {
    if (currentProject && currentProject?.id) {
      dispatch(destinationsAvalibleForRoute(currentProject.id));
    }
  }, [dispatch, currentProject]);

  useEffect(() => {
    if (destinationsList.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [destinationsList]);

  const selectedLabel = useMemo(() => {
    if (destinationsList.length === 0) return "No hay destinos disponibles";
    if (selectedCount === 0) return "Selecciona";
    return `${selectedCount} seleccionados`;
  }, [selectedCount, destinationsList]);

  const selectedOptions = useMemo(() => {
    return destinationsList.filter((o) => selectedIds.has(o.destionationId));
  }, [selectedIds, destinationsList]);

  const removeId = (destionationId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(destionationId);
      return next;
    });
  };

  const eraseInfo = () => {
    setData((old) => ({
      ...old,
      routeCode: null,
      route: null,
      destinations: [],
      status: true,
    }));
    setOpenModal(false);
    setSelectedIds(new Set([]));
  };

  const handleOnSubmit = async () => {
    if (state.$all_errors.length > 0 || state.$all_source_errors.length > 0) {
      setShowError(true);
      validate();
      notify("error", t("error.general_toast"), 1200);
      return;
    }
    if (state.$data.destinations.length > 0 && state.$data.status === false) {
      setShowError(true);
      notify(
        "error",
        "No se pueden asignar destinos a una ruta inactiva",
        2000
      );
      return;
    }
    setIsLoad(true);
    if (buttonRef && buttonRef.current) buttonRef.current.disabled = true;
    let isSuccess = await dispatch(
      addOrUpdateRoute({
        ...state.$data,
        id: routeMemo?.id,
        projectId: currentProject.id,
      })
    );
    if (buttonRef && buttonRef.current) buttonRef.current.disabled = false;
    setIsLoad(false);
    setShowError(false);
    if (isSuccess) {
      dispatch(getRoutesList(currentProject.id));
      eraseInfo();
    }
  };

  return (
    <Modal
      openModal={openModal}
      closeModal={() => eraseInfo()}
      titleModal={"Rutas"}
      onSubmit={handleOnSubmit}
      size="lg"
      disableAdd={state.$invalid}
      processFetch={isLoad}
      buttonRef={buttonRef}
    >
      <div className="flex flex-wrap gap-4 justify-between">
        {routeMemo && (
          <InputCustom
            type={"number"}
            labelText={"Id Ruta:"}
            value={routeMemo?.id}
            disabled={true}
          />
        )}
        <InputCustom
          type={"text"}
          labelText={t("route.code_route")}
          errorMessage={
            showError && state.$errors.routeCode.length > 0
              ? state.$errors.routeCode[0].$message
              : null
          }
          onBlur={() => setExplicitField("routeCode", true)}
          onChange={(e) => {
            e.persist();
            setData((old) => ({
              ...old,
              routeCode: e.target.value,
            }));
          }}
          value={state.$data.routeCode}
        />
        <InputCustom
          type={"select"}
          labelText={t("route.status")}
          onChange={(e) => {
            e.persist();
            setData((old) => ({
              ...old,
              status: e.target.value === "true",
            }));
          }}
          options={[
            { text: tg("select.active"), value: true },
            { text: tg("select.inactive"), value: false },
          ]}
          value={state.$data.status}
          onBlur={() => setExplicitField("status", true)}
          keyId="status"
          disabled={!routeMemo}
        />
        <InputCustom
          size="fullWidth"
          type={"text"}
          labelText={t("route.route")}
          errorMessage={
            showError && state.$errors.route.length > 0
              ? state.$errors.route[0].$message
              : null
          }
          onBlur={() => setExplicitField("route", true)}
          onChange={(e) => {
            e.persist();
            setData((old) => ({
              ...old,
              route: e.target.value,
            }));
          }}
          value={state.$data.route}
        />
        <div className="w-full mt-2">
          <div className="mb-1 text-sm font-semibold text-slate-700">
            Destinos
          </div>

          {/* contenedor 2 columnas: lista + chips */}
          <div className="flex items-start gap-4 ">
            {/* IZQUIERDA */}
            <div className="w-[260px] rounded-md border border-slate-200 bg-white">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={[
                  "w-full",
                  "bg-gray-200",
                  "flex items-center justify-between gap-2",
                  "rounded-t-md",
                  "rounded-b-none",
                  "bg-slate-100",
                  "px-3 py-2",
                  "text-left text-sm text-slate-700",
                  "hover:bg-slate-200",
                  "focus:outline-none focus:ring-2 focus:ring-slate-300",
                ].join(" ")}
              >
                <span className="truncate">{selectedLabel}</span>

                <span
                  className={[
                    "shrink-0 text-slate-600",
                    "transition-transform duration-200",
                    open ? "rotate-180" : "rotate-0",
                  ].join(" ")}
                >
                  ▼
                </span>
              </button>

              {open && (
                <div className="max-h-60 overflow-auto p-2">
                  {destinationsList.map((opt) => {
                    const checked = selectedIds.has(opt.destionationId);

                    return (
                      <div
                        key={opt.destionationId}
                        className="flex items-center justify-between gap-2 px-1 py-1"
                      >
                        <span className="text-sm text-slate-700">
                          {opt.shipToName}
                        </span>

                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleOption(opt.destionationId)}
                          className="h-4 w-4"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* DERECHA */}
            <div className="flex-1 min-w-0">
              <div className="min-h-[44px] w-full rounded-md border border-slate-200 bg-gray-200 p-2">
                <div className="flex flex-wrap gap-2">
                  {selectedOptions.length === 0 ? (
                    <span className="px-2 py-1 text-sm text-slate-400">
                      Sin destinos seleccionados
                    </span>
                  ) : (
                    selectedOptions.map((opt) => (
                      <span
                        key={opt.destionationId}
                        className={[
                          "inline-flex items-center gap-2",
                          "rounded-full border border-slate-200 bg-slate-100",
                          "px-3 py-1",
                          "text-sm text-slate-800",
                          "bg-white",
                          "border border-black/20",
                        ].join(" ")}
                      >
                        <span className="max-w-[240px] truncate">
                          {opt.shipToName}
                        </span>

                        <button
                          type="button"
                          onClick={() => removeId(opt.destionationId)}
                          className={[
                            "inline-flex h-5 w-5 items-center justify-center",
                            "rounded-full",
                            "text-slate-700",
                            "hover:bg-slate-200",
                            "focus:outline-none focus:ring-2 focus:ring-slate-300",
                          ].join(" ")}
                          aria-label={`Eliminar ${opt.shipToName}`}
                          title="Eliminar"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>
                {showError && state.$errors.destinations.length > 0 && (
                  <div className=" px-2 text-xs text-red-500 mb-2">
                    {state.$errors.destinations[0].$message}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
export default RouteModal;
